import type { StatusTone } from '@/components/ri/statusTones';
import { formatInEventZone } from './attendance';
import type { EventCaptureState } from './offline/captureRuntime';
import type { EventActivity, EventRegistration, EventWorkspace } from './types';

export type EventMobileView = 'event' | 'scan' | 'queue' | 'leaders' | 'attendance' | 'activity' | 'exceptions' | 'integrations';
export type EventDesktopTab = 'overview' | 'attendees' | 'exceptions' | 'demos' | 'leaderboard' | 'setup';
export type EventMobileTab = 'event' | 'scan' | 'queue' | 'leaders';

// The menu's views (attendance, activity, exceptions, integrations) sit under the Event tab. With no
// tab current, Scan's soft pill was the only accent on the bar and read as the open tab.
export function mobileTabFor(view: EventMobileView): EventMobileTab {
  return view === 'scan' || view === 'queue' || view === 'leaders' ? view : 'event';
}

export const EVENT_TAB_PARAM = 'tab';
// Phone Event-tab steps (attendee list search, name search, a brief) live in the URL so system back closes them.
export const EVENT_PANEL_PARAM = 'panel';
export type EventPanel = 'search' | 'name' | 'brief';

// A name and company already known when the rep lands on the name search (read or typed on the Scan
// tab), with the email and badge ID the scan had so the search doesn't lose them.
export interface NamePrefill { name: string; company: string; email?: string | null; badgeId?: string | null }

export function eventPanelFromParam(value: string | null): EventPanel | null {
  return value === 'search' || value === 'name' || value === 'brief' ? value : null;
}

const MOBILE_ALIASES: Record<string, EventMobileView> = {
  capture: 'event', attendees: 'event', overview: 'event', activities: 'attendance', leaderboard: 'leaders', setup: 'integrations',
};
const DESKTOP_ALIASES: Record<string, EventDesktopTab> = {
  capture: 'attendees', event: 'attendees', scan: 'attendees', queue: 'attendees', activities: 'overview', attendance: 'overview',
  activity: 'overview', leaders: 'leaderboard', integrations: 'setup',
};
const DESKTOP_TABS: EventDesktopTab[] = ['overview', 'attendees', 'exceptions', 'demos', 'leaderboard', 'setup'];
// Lead-only, like the outcomes export the Demos tab reads from.
const MANAGER_TABS = new Set<EventDesktopTab>(['demos', 'setup']);

// Views that need the signed-in server; the local-only offline workspace hides them.
const ONLINE_ONLY = new Set<string>(['activity', 'exceptions', 'overview', 'leaderboard', 'demos']);

// `leaderboard: false` when the lead keeps the leaderboard to themselves.
export interface EventViewAccess { canManage: boolean; localOnly: boolean; leaderboard?: boolean }

export function mobileViewFromParam(value: string | null, access: EventViewAccess): EventMobileView {
  const view = (value && (MOBILE_ALIASES[value] ?? value)) as EventMobileView | null;
  const known: EventMobileView[] = ['event', 'scan', 'queue', 'leaders', 'attendance', 'activity', 'exceptions', 'integrations'];
  if (!view || !known.includes(view)) return 'event';
  if (view === 'integrations' && !access.canManage) return 'event';
  if (view === 'leaders' && access.leaderboard === false) return 'event';
  if (access.localOnly && ONLINE_ONLY.has(view)) return 'event';
  return view;
}

export function desktopTabsFor(access: EventViewAccess): EventDesktopTab[] {
  return DESKTOP_TABS.filter((tab) => (!MANAGER_TABS.has(tab) || access.canManage) && (tab !== 'leaderboard' || access.leaderboard !== false)
    && !(access.localOnly && ONLINE_ONLY.has(tab)));
}

export function desktopTabFromParam(value: string | null, access: EventViewAccess): EventDesktopTab {
  const tabs = desktopTabsFor(access);
  const tab = (value && (DESKTOP_ALIASES[value] ?? value)) as EventDesktopTab | null;
  if (tab && tabs.includes(tab)) return tab;
  const preferred: EventDesktopTab = access.canManage ? 'overview' : 'attendees';
  return tabs.includes(preferred) ? preferred : tabs[0];
}

const PHASE_ORDER: Record<EventPhase, number> = { eventlive: 0, eventupcoming: 1, eventended: 2 };

export function sortEventsByPhase<T extends Pick<EventWorkspace, 'startsAt' | 'endsAt'>>(events: readonly T[], now = Date.now()): T[] {
  return [...events].sort((a, b) => {
    const phaseA = eventPhase(a, now);
    const byPhase = PHASE_ORDER[phaseA] - PHASE_ORDER[eventPhase(b, now)];
    if (byPhase) return byPhase;
    return phaseA === 'eventended' ? Date.parse(b.endsAt) - Date.parse(a.endsAt) : Date.parse(a.startsAt) - Date.parse(b.startsAt);
  });
}

export type EventPhase = 'eventlive' | 'eventupcoming' | 'eventended';

export function eventPhase(item: Pick<EventWorkspace | EventActivity, 'startsAt' | 'endsAt'>, now = Date.now()): EventPhase {
  if (now < Date.parse(item.startsAt)) return 'eventupcoming';
  if (now > Date.parse(item.endsAt)) return 'eventended';
  return 'eventlive';
}

export function activityRegistered(activityId: string, registrations: EventRegistration[] | undefined): number {
  return registrations?.filter((r) => r.activityId === activityId && !r.localPending && r.status === 'Registered').length ?? 0;
}

export function activityCapacityStatus(activity: EventActivity, registered: number): 'eventopen' | 'eventfull' | null {
  if (activity.type === 'BoothScan') return null;
  return activity.capacity !== null && registered >= activity.capacity ? 'eventfull' : 'eventopen';
}

// Dates read at the venue, like activity times: a late-evening close must not roll into the next day in another zone.
export function formatEventDates(startsAt: string, endsAt: string, timeZone?: string | null): string {
  const day: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  const at = (value: string, options: Intl.DateTimeFormatOptions) => formatInEventZone(value, options, timeZone);
  const dayKey = (value: string) => at(value, { year: 'numeric', month: 'numeric', day: 'numeric' });
  if (dayKey(startsAt) === dayKey(endsAt)) return at(startsAt, { ...day, year: 'numeric' });
  const sameYear = at(startsAt, { year: 'numeric' }) === at(endsAt, { year: 'numeric' });
  return `${at(startsAt, sameYear ? day : { ...day, year: 'numeric' })} – ${at(endsAt, { ...day, year: 'numeric' })}`;
}

// A waitlist only applies once a capacity can fill up.
export function activityRegistrationLabel(activity: Pick<EventActivity, 'type' | 'registrationEnabled' | 'waitlistEnabled' | 'capacity'>): string | null {
  if (activity.type === 'BoothScan') return null;
  if (!activity.registrationEnabled) return 'Closed';
  return activity.waitlistEnabled && activity.capacity !== null ? 'Open · waitlist' : 'Open';
}

export function pendingOperationCount(state: EventCaptureState | null): number {
  return state?.operations.filter((operation) => operation.state !== 'completed').length ?? 0;
}

export interface EventSyncStatus { label: string; tone: StatusTone }

export function eventSyncStatus(state: EventCaptureState | null, online: boolean, localOnly: boolean): EventSyncStatus {
  const pending = pendingOperationCount(state);
  if (state?.requiresReauthorization) return { label: 'Access check needed', tone: 'warning' };
  if (state?.prepared && !state.unlocked) return { label: 'Offline data locked', tone: 'neutral' };
  if (!online || localOnly) return { label: `Offline · ${pending} queued`, tone: 'warning' };
  if (pending > 0) {
    const review = state?.operations.some((operation) => operation.state === 'needs-review');
    return { label: `${pending} pending`, tone: review ? 'danger' : 'warning' };
  }
  return { label: 'Online · synced', tone: 'success' };
}

export type EventManageAction = 'settings' | 'add-activity' | 'edit-activity';
